/**
 * Locale-aware number/date formatting for the app's numeral policy: Arabic UI
 * uses Arabic month/day names and م/ص markers but Western (Latin) digits —
 * the Egyptian convention. Replaces @eduact/utils' NumberFormatter (which
 * emits Arabic-Indic digits) and DateFormatter (whose locale mapping predates
 * the 'ar-EG' storage format and silently falls back to en-GB for Arabic).
 */

const resolveLocale = () => {
	const lang = (document.documentElement.lang || 'en-GB').toLowerCase()
	// '-u-nu-latn' pins the Latin numbering system while keeping Arabic formatting
	return lang.startsWith('ar') ? 'ar-EG-u-nu-latn' : 'en-GB'
}

export const NumberFormatter = (number: number | string, options?: Intl.NumberFormatOptions): string => {
	return new Intl.NumberFormat([resolveLocale()], options).format(Number(number))
}

/**
 * Learning-time durations: the top TWO nonzero units ("2h 15m"), bare seconds
 * under a minute ("45s"), "0" for nothing recorded. Unit labels follow the
 * active language (en: y/d/h/m/s — ar: سنة/يوم/س/د/ث); digits stay Latin per
 * the app's numeral policy.
 */
export const formatDuration = (totalSeconds: number): string => {
	const total = Math.floor(Number(totalSeconds) || 0)
	if (total <= 0) return '0'
	const lang = (document.documentElement.lang || 'en-GB').toLowerCase()
	const labels = lang.startsWith('ar') ? ['سنة', 'يوم', 'س', 'د', 'ث'] : ['y', 'd', 'h', 'm', 's']
	if (total < 60) return `${total}${labels[4]}`
	const values = [
		Math.floor(total / 31536000),
		Math.floor((total % 31536000) / 86400),
		Math.floor((total % 86400) / 3600),
		Math.floor((total % 3600) / 60),
		total % 60,
	]
	return values
		.map((value, i) => ({ value, label: labels[i] }))
		.filter(part => part.value > 0)
		.slice(0, 2)
		.map(part => `${part.value}${part.label}`)
		.join(' ')
}

type DateFormatterOptions = Intl.DateTimeFormatOptions & { withTime?: boolean }

export const DateFormatter = (date: string | number | Date, options?: DateFormatterOptions): string => {
	if (!date) throw new Error("Date isn't provided")
	const { withTime, ...intlOptions } = options ?? {}
	return new Intl.DateTimeFormat([resolveLocale()], {
		day: 'numeric',
		month: 'numeric',
		hour: withTime ? 'numeric' : undefined,
		...intlOptions,
	}).format(new Date(date))
}
